import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import InstallQR from '@/components/InstallQR';
import JumpToNav from '@/components/JumpToNav';
import { LIFECYCLE_QUADRANTS, LifecycleSlideOver } from '@/components/lifecycle/LifecycleSlideOver';
import { LAUNCHES as FEATURE_LAUNCHES } from '@/data/featureLaunches';
import SecurityNoticeBanner from '@/components/SecurityNoticeBanner';
import {
  Globe, Calendar, MapPin, Shield, Zap, Users, Crown, Database,
  ArrowRight, CheckCircle, ChevronRight, Stethoscope, LogIn,
  Search, TrendingUp, FileText, BarChart3, Eye, MessageCircle, Rocket, QrCode, Phone, Share2, Newspaper,
  Sparkles, HeartPulse, BookOpen, Building2, Star,
} from 'lucide-react';


const FEATURES = [
  { icon: Globe, title: 'Your Personal Website', desc: 'A professional website branded entirely for you — not a listing on someone else\'s platform. Your brand, your patients, your terms.' },
  { icon: Calendar, title: 'Online Booking', desc: 'Patients book directly from your website. No calls, no middlemen taking a cut. You control every slot.' },
  { icon: MapPin, title: 'Multi-Location Support', desc: 'Practice at multiple clinics? Show location-wise schedule and availability. Patients find you wherever you practice.' },
  { icon: FileText, title: 'In Your Own Words (Manual on Free)', desc: 'Free-tier doctors hand-write their bio, procedures and articles — no AI templates. Coffee Premium adds a one-time AI on-ramp; TurboCharge unlocks monthly AI drafts. Always your voice, never generic.' },
  { icon: Search, title: 'Auto-SEO Engine', desc: 'Every page is structured for search engines. AI generates meta tags, Schema.org markup, and sitemap entries. Your name rises organically.' },
  { icon: Shield, title: 'Doctor-Controlled', desc: 'You own your brand, your patients, your data. No commercial rankings. No pay-to-appear. Skills speak, not ad spend.' },
  { icon: TrendingUp, title: 'Visibility Accelerator', desc: 'Guided tasks to build Google credibility fast. 5 simple link placements that tell search engines you\'re a real, practicing doctor.' },
  { icon: Share2, title: 'Patient Referral Engine', desc: 'After every appointment, patients can refer you with a tracked link. Your best patients become your growth engine — organically.' },
  { icon: Newspaper, title: 'Medical Insights Hub', desc: 'Your articles appear on a public knowledge hub alongside other specialists. Patients discover you through the content you write.' },
];

const STEPS = [
  { num: '01', title: 'Sign Up & Set Up', desc: 'Create your account in 30 seconds. Add your photo, bio, and procedures.' },
  { num: '02', title: 'Go Live', desc: 'Your SEO-optimized website is live. Share it on WhatsApp, social media, or your clinic.' },
  { num: '03', title: 'Grow Organically', desc: 'Publish case studies monthly. Patients find you on Google. Track Your Appointments. Expand Your Practice Faster.' },
];

export default function SaaSLandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [content, setContent] = useState(null);
  const [openStage, setOpenStage] = useState(null);

  useEffect(() => {
    if (!loading && user?.role === 'doctor') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get('/public/latest-content').then(r => setContent(r.data)).catch(() => {});
  }, []);

  const stats = content?.stats || {};

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — Generate Your Own Medical Website. Highlight Your Medical Expertise Online.</title>
        <meta name="description" content="DocSite gives every doctor an SEO-optimized personal website. Patients find you on Google through your own medical expertise. 15 minutes a month is all it takes." />
      </Helmet>

      {/* Security notice — anchored at the very top so visitors who get the
          site forwarded see it before anything else. Persistent (not dismissible). */}
      <SecurityNoticeBanner />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-sky-50/30" data-testid="saas-hero">
        {/* Dotted grid texture */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0284C7 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        {/* Ambient gutter blobs (Option E — make whitespace feel designed) */}
        <div className="hidden lg:block absolute -left-32 top-1/4 w-96 h-96 rounded-full bg-sky-200/30 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="hidden lg:block absolute -right-24 bottom-0 w-[28rem] h-[28rem] rounded-full bg-sky-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
        {/* LEFT watermark — reads UPWARDS (bottom → top), single line, subtle */}
        <div className="hidden xl:block absolute left-2 top-1/2 text-[1.75rem] uppercase tracking-[0.05em] text-sky-700/30 font-semibold pointer-events-none select-none" style={{ writingMode: 'vertical-rl', whiteSpace: 'nowrap', transform: 'translateY(-50%) rotate(180deg)', fontFamily: 'Outfit, sans-serif' }} aria-hidden="true">Specialist Clinical Skills Speak, Not Ad Spends</div>
        {/* RIGHT watermark — reads DOWNWARDS, single line, subtle */}
        <div className="hidden xl:block absolute right-2 top-1/2 text-[1.75rem] uppercase tracking-[0.05em] text-sky-700/30 font-semibold pointer-events-none select-none" style={{ writingMode: 'vertical-rl', whiteSpace: 'nowrap', transform: 'translateY(-50%)', fontFamily: 'Outfit, sans-serif' }} aria-hidden="true">Democratizing Visibility</div>

        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 pt-6 md:pt-8 pb-3 md:pb-4 relative lg:min-h-[calc(100vh-8rem)] flex flex-col">
          <div className="flex-1 min-h-0">
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-10 items-start">
              <div className="space-y-5 min-w-0">
              <div>
                <h1 className="tracking-tight font-light text-slate-900 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.625rem, 2.6vw, 2.75rem)' }} data-testid="hero-h1">
                  Highlight Your Medical Expertise Online.
                  <span className="block font-medium mt-2 text-slate-700">Patients Are Searching For You.</span>
                </h1>
              </div>

              {/* Equation chain — 3 boxes + operators */}
              <div className="space-y-3">
                {/* Box 1 — keynote formula with sub-line descriptions */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-4 rounded-2xl bg-sky-100/80 border border-sky-300 max-w-full" data-testid="hero-equation" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.05rem, 1.1vw, 1.275rem)' }}>
                  <div className="flex flex-col items-start leading-tight">
                    <span className="font-semibold text-slate-900">Get Your Permanent Digital Website</span>
                    <span className="text-slate-500 mt-0.5" style={{ fontSize: '0.78em' }}>Created, Hosted, Maintained</span>
                  </div>
                  <span className="text-slate-500 font-bold self-center" style={{ fontSize: '1.15em' }}>×</span>
                  <div className="flex flex-col items-start leading-tight">
                    <span className="font-semibold text-slate-900">Your Clinical Skill</span>
                    <span className="text-slate-500 mt-0.5" style={{ fontSize: '0.78em' }}>Procedures, Case Studies,<br />Content Management Platform</span>
                  </div>
                  <span className="text-slate-500 font-bold self-center" style={{ fontSize: '1.15em' }}>×</span>
                  <div className="flex flex-col items-start leading-tight">
                    <span className="font-semibold text-slate-900">Direct Digital Reach</span>
                    <span className="text-slate-500 mt-0.5" style={{ fontSize: '0.78em' }}>Visibility Propagation Engine</span>
                  </div>
                  <span className="text-slate-500 font-bold self-center" style={{ fontSize: '1.15em' }}>×</span>
                  <div className="flex flex-col items-start leading-tight self-center">
                    <span className="font-semibold text-slate-900">No Aggregators</span>
                  </div>
                  <span className="text-slate-500 font-bold self-center" style={{ fontSize: '1.15em' }}>=</span>
                  <div className="flex flex-col items-start leading-tight self-center">
                    <span className="font-bold text-sky-700">Patient Trust</span>
                  </div>
                </div>

                {/* + operator (with breath above) */}
                <div className="flex items-center gap-3 max-w-xl pt-4 md:pt-5" data-testid="hero-operator-plus">
                  <div className="flex-1 h-px bg-sky-300"></div>
                  <div className="w-9 h-9 rounded-full bg-white border-2 border-sky-400 flex items-center justify-center font-bold text-sky-600 shadow-sm leading-none" style={{ fontSize: '1.25rem' }}>+</div>
                  <div className="flex-1 h-px bg-sky-300"></div>
                </div>

                {/* Box 2 = Box 3 — horizontal row */}
                <div className="flex flex-wrap items-stretch gap-3" data-testid="hero-outcome-row" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.05rem, 1.15vw, 1.32rem)' }}>
                  <div className="flex-1 min-w-[260px] inline-flex items-center px-5 py-3 rounded-2xl bg-sky-200/70 border border-sky-400" data-testid="hero-tagline-1">
                    <span className="font-medium text-slate-900">Get <span className="font-bold text-sky-800">Unlimited Patient Appointments &amp; Consults</span> — over time, directly.</span>
                  </div>
                  <div className="flex items-center self-stretch" data-testid="hero-operator-equals">
                    <div className="w-10 h-10 rounded-full bg-white border-2 border-sky-600 flex items-center justify-center font-bold text-sky-700 shadow-sm leading-none" style={{ fontSize: '1.35rem' }}>=</div>
                  </div>
                  <div className="flex-1 min-w-[260px] inline-flex items-center px-5 py-3.5 rounded-2xl bg-sky-300/70 border-2 border-sky-600 shadow-sm" data-testid="hero-tagline-2">
                    <span className="font-semibold text-slate-900">Get a <span className="font-bold">Future Ready Practice Management Platform</span>, At <span className="font-bold text-sky-800">Near 0 Costs, Forever!</span></span>
                  </div>
                </div>

                {/* === Doctor Clinical Practice Lifecycle band — inside hero, in left column === */}
                <div id="lifecycle" className="pt-5 md:pt-6 scroll-mt-24" data-testid="lifecycle-band">
                  <div className="mb-3 md:mb-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Sparkles size={16} className="text-sky-600" />
                      <span className="text-sm uppercase tracking-[0.2em] font-bold text-sky-700">
                        And it covers every stage of your practice
                      </span>
                    </div>
                    <p className="text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.125rem, 1.7vw, 1.625rem)' }}>
                      <span className="font-medium">DocSite is the first global platform to deliver all this in one go — </span>
                      <span className="font-light text-slate-700">across every <span className="font-medium text-slate-900">Doctor Clinical Practice Lifecycle</span> need.</span>
                    </p>
                  </div>
                  <div
                    className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-3.5 relative lg:w-[calc(100%_+_15rem)]"
                    style={{ zIndex: 2 }}
                    data-testid="lifecycle-tiles-grid"
                  >
                    {LIFECYCLE_QUADRANTS.map((q, idx) => {
                      const Icon = q.icon;
                      return (
                        <button
                          key={q.label}
                          onClick={() => setOpenStage(q)}
                          className={`group relative bg-gradient-to-br ${q.bg} rounded-xl border ${q.border} p-5 flex flex-col gap-3 hover:shadow-md hover:-translate-y-0.5 transition-all text-left focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400`}
                          data-testid={`lifecycle-tile-${idx + 1}`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <div className={`w-12 h-12 rounded-lg ${q.badge} flex items-center justify-center`}>
                              <Icon size={22} />
                            </div>
                            <span className={`text-[13px] uppercase tracking-[0.14em] font-bold ${q.accent}`}>{q.stage}</span>
                          </div>
                          <h5 className="font-semibold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.125rem, 1.4vw, 1.375rem)' }}>
                            {q.label}
                          </h5>
                          <ul className="space-y-1.5 mt-auto">
                            {q.needs.slice(0, 2).map(n => (
                              <li key={n} className="flex items-start gap-2.5">
                                <span className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${q.bullet}`} />
                                <span className={`text-[17px] font-semibold ${q.accent} leading-snug`} style={{ fontFamily: 'Outfit, sans-serif' }}>{n}</span>
                              </li>
                            ))}
                            {q.needs.length > 2 && (
                              <li className={`text-[15px] ${q.accent} opacity-75 ml-4 italic`}>+{q.needs.length - 2} more</li>
                            )}
                          </ul>
                          <div className={`pt-3 mt-1 border-t border-slate-200/60 inline-flex items-center gap-1.5 text-[15px] font-semibold ${q.accent}`}>
                            See what you get <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* CTAs (mobile only — desktop versions live under QR in right column) */}
              <div className="flex flex-wrap gap-3 pt-2 lg:hidden">
                <button onClick={() => navigate('/register?as=doctor&from=landing_hero')} className="rounded-full px-8 py-4 bg-sky-600 text-white font-medium hover:bg-sky-700 transition-all hover:shadow-lg hover:shadow-sky-600/25 flex items-center gap-2" style={{ fontSize: 'clamp(1rem, 1.05vw, 1.2rem)' }} data-testid="hero-get-started-button-mobile">
                  Get Started in 3 Clicks, FREE <ArrowRight size={20} />
                </button>
                <button onClick={() => navigate('/brochure')} className="rounded-full px-8 py-4 bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors border border-slate-300 flex items-center gap-2" style={{ fontSize: 'clamp(1rem, 1.05vw, 1.2rem)' }} data-testid="hero-brochure-button-mobile">
                  <FileText size={18} /> Product Brochure
                </button>
              </div>
            </div>

            <div className="hidden lg:block lg:mt-4">
              <div className="relative" style={{ fontSize: 'clamp(0.8rem, 0.9vw, 1.05rem)' }}>
                <div className="absolute -top-8 -right-8 w-80 h-80 bg-sky-100/50 rounded-full blur-3xl" />
                <div className="relative z-10 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-700 px-4 py-2.5 flex items-center gap-2">
                    <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-yellow-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
                    <span className="text-slate-400 ml-2" style={{ fontSize: '0.75em' }}>docsite.com/dr/dr-rajesh-sharma</span>
                  </div>
                  <div className="p-[1.5em] space-y-[1em]">
                    <div className="flex items-center gap-[1em]">
                      <img src="https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMG1hbGUlMjBkb2N0b3IlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYwOTgxNTV8MA&ixlib=rb-4.1.0&q=85" alt="Demo" className="rounded-xl object-cover" style={{ width: '4em', height: '4em' }} />
                      <div>
                        <p className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.05em' }}>Dr. Rajesh Sharma</p>
                        <p className="text-sky-600" style={{ fontSize: '0.85em' }}>Cardiology | 15 yrs exp</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {['Mon', 'Tue', 'Wed'].map(d => (
                        <div key={d} className="bg-sky-50 rounded-lg px-2 py-2 text-center">
                          <p className="text-sky-600 font-medium" style={{ fontSize: '0.7em' }}>{d}</p>
                          <p className="text-slate-600" style={{ fontSize: '0.8em' }}>10:00-13:00</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-sky-600 text-white text-center py-2.5 rounded-lg font-medium" style={{ fontSize: '0.95em' }}>Book Appointment</div>
                    <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2">
                      <Search size={12} className="text-emerald-600" />
                      <span className="text-emerald-700 font-medium" style={{ fontSize: '0.7em' }}>Indexed by Google — showing in search results</span>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-3 -left-3 bg-white rounded-xl shadow-lg p-3 border border-slate-100 z-20">
                  <div className="flex items-center gap-2">
                    <Stethoscope size={16} className="text-sky-600" />
                    <span className="font-medium text-slate-700" style={{ fontSize: '0.8em' }}>Any Specialization</span>
                  </div>
                </div>
              </div>

              {/* Install-the-app QR — right-aligned to the Dr. Rajesh card's right border */}
              <div className="mt-3 flex justify-end">
                <InstallQR testId="landing-install-qr" />
              </div>

              {/* CTAs moved here from left column (desktop only) — sits below QR, right-aligned to QR's right border */}
              <div className="hidden lg:flex mt-5 flex-col items-end gap-3">
                <button onClick={() => navigate('/register?as=doctor&from=landing_hero')} className="w-full max-w-[20rem] rounded-full px-6 py-3.5 bg-sky-600 hover:bg-sky-700 text-white font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all" style={{ fontSize: 'clamp(0.95rem, 1vw, 1.1rem)' }} data-testid="hero-get-started-button">
                  Get Started in 3 Clicks, FREE <ArrowRight size={18} />
                </button>
                <button onClick={() => navigate('/brochure')} className="w-full max-w-[20rem] rounded-full px-6 py-3.5 bg-white text-slate-700 font-medium border border-slate-300 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2" style={{ fontSize: 'clamp(0.95rem, 1vw, 1.1rem)' }} data-testid="hero-brochure-button">
                  <FileText size={16} /> Product Brochure
                </button>
                <button onClick={() => navigate('/dr/dr-rajesh-sharma')} className="text-sky-600 hover:text-sky-700 transition-colors flex items-center gap-1.5 font-medium underline-offset-4 hover:underline mt-1" style={{ fontSize: '0.875rem' }} data-testid="hero-demo-button">
                  See a real doctor's DocSite practice <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>
          </div>

          {/* Trust row + Jump-to nav — both anchored at the bottom of the hero, side-by-side spacing */}
          <div className="mt-6 pt-4 border-t border-slate-200/70 space-y-4">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-slate-600 font-medium" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.2rem)' }} data-testid="hero-trust-row">
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Free to start</span>
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> No credit card</span>
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Live in 5 min</span>
            </div>

            <JumpToNav
              testId="home-jump-nav"
              className="!mt-0 !pt-0 !border-0"
              items={[
                { icon: Search, label: 'SEO Advantage', href: '#seo-advantage', color: 'rose' },
                { icon: Sparkles, label: 'Practice Lifecycle', href: '#lifecycle', color: 'teal' },
                { icon: Zap, label: 'Features', href: '#features', color: 'sky' },
                { icon: Calendar, label: 'How it works', href: '#how-it-works', color: 'emerald' },
                { icon: Rocket, label: 'TurboCharge', href: '#turbocharge', color: 'violet' },
                { icon: Users, label: 'For Patients', to: '/for-patients', color: 'amber' },
                { icon: Stethoscope, label: 'Try a sample doctor', to: '/dr/dr-rajesh-sharma', color: 'fuchsia' },
              ]}
            />
          </div>
        </div>
      </section>

      <WhatsNewStrip />

      <LifecycleSlideOver stage={openStage} onClose={() => setOpenStage(null)} />

      {/* How Search Engines See Your Content */}
      <section id="seo-advantage" className="py-16 md:py-24 bg-gradient-to-br from-sky-50/80 via-white to-slate-50 scroll-mt-24" data-testid="seo-value-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">The SEO Advantage</p>
            <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 3rem)' }}>
              Your Medical Expertise Reflected Online.<br className="hidden sm:block" />
              Noticed by Search Engines. Found by Patients.
            </h2>
            <p className="text-base text-slate-600 mt-4 max-w-2xl mx-auto">
              When you share your real clinical knowledge on DocSite, search engines index it. Patients searching for your specialty in your city — discover you organically. No ad spend. No middlemen.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FileText, title: 'AI-Written SEO Articles You Own (TurboCharge)', desc: 'Two SEO-ready articles auto-drafted every month — TurboCharge tier only. Coffee Premium gets a one-time AI on-ramp for bio/procedures; Free doctors hand-write everything. You review, edit, one-click publish — every edit audit-trailed. The content is yours forever.', color: 'sky' },
              { icon: QrCode, title: 'Printable Clinic QR Kit', desc: 'A4 poster + sticker pack with your branded QR. Patients scan at your clinic — your website opens, even offline on repeat visits. TurboCharge.', color: 'emerald' },
              { icon: Phone, title: '"Save My Details" Live Widget', desc: 'A floating QR on your public site — patients tap once to save your contact offline or open your site on another device. Built for in-clinic moments. TurboCharge.', color: 'violet' },
              { icon: TrendingUp, title: 'Clinic-QR Lead Attribution', desc: 'Scans from your clinic poster show up separately in My Leads. See exactly which patients walked in and booked online later.', color: 'amber' },
            ].map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-7 hover:shadow-md hover:border-sky-200 transition-all">
                <div className={`w-12 h-12 rounded-xl bg-${color}-50 flex items-center justify-center mb-4`}>
                  <Icon size={22} className={`text-${color}-600`} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* You are the Brand — ownership narrative (new, from Figma) */}
      <section className="py-20 md:py-28 bg-white border-y border-slate-100" data-testid="you-are-the-brand-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-sky-600 mb-4">Why DocSite exists</p>
          <h2 className="font-light tracking-tight text-slate-900 leading-[1.08] max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 3vw, 3.5rem)' }}>
            You are the brand.<br />
            <span className="text-slate-500">Patients come to you — not to an aggregator's listing page.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 mt-12 md:mt-16">
            <div>
              <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <Crown size={16} className="text-emerald-600" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Brand, My URL</h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">Your DocSite lives at your own clean URL — not behind a directory's paywall. Share it on Google, LinkedIn, WhatsApp, printed handouts. You own it forever.</p>
            </div>
            <div>
              <div className="w-9 h-9 rounded-full bg-sky-50 flex items-center justify-center mb-4">
                <Users size={16} className="text-sky-600" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Patients, My Relationship</h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">Every booking and message flows directly to you. No middleman, no commission, no rented patient list. The patient's trust is with you — as it should be.</p>
            </div>
            <div>
              <div className="w-9 h-9 rounded-full bg-violet-50 flex items-center justify-center mb-4">
                <Database size={16} className="text-violet-600" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Data, My Export</h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">Your patient-interaction data is encrypted at rest with Fernet (AES-128) and sealed by a passphrase only you know. One click exports the full archive. Walk away anytime, take everything.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 md:py-32 bg-white scroll-mt-24" data-testid="features-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Everything You Need</p>
            <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 3rem)' }}>
              More Patients Find You. Organically.
            </h2>
            <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
              DocSite brings the doctor–patient conversation back to the specialist. Your real medical expertise — reflected on your DocSite practice surface and indexed by search engines — becomes the reason patients reach you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-7 hover:-translate-y-1 hover:shadow-md hover:border-sky-200 transition-all duration-300" data-testid={`feature-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-sky-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest from Doctors — Live Content Feed */}
      {content?.latest_studies?.length > 0 && (
        <section className="py-16 md:py-24 bg-slate-50/80" data-testid="latest-content-section">
          <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Fresh From Doctors</p>
              <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 3rem)' }}>
                Latest from Doctors on DocSite
              </h2>
              <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
                Real doctors sharing real medical expertise. Every post is an indexed page on Google.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {content.latest_studies.map(study => (
                <a key={study.id} href={`/dr/${study.doctor_slug}`}
                  className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:border-sky-200 transition-all block group"
                  data-testid={`latest-study-${study.id}`}>
                  <p className="text-xs text-sky-600 font-medium mb-2">{study.doctor_name}</p>
                  <h3 className="text-base font-medium text-slate-900 mb-2 group-hover:text-sky-700 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {study.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{study.preview}</p>
                  <p className="text-[10px] text-slate-400 mt-3">
                    {study.published_at ? new Date(study.published_at).toLocaleDateString('en', { month: 'long', year: 'numeric' }) : ''}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Platform Stats */}
      {stats.doctors > 0 && (
        <section className="py-12 bg-white border-y border-slate-100" data-testid="platform-stats">
          <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: stats.doctors, label: 'Doctors on Platform', color: 'sky' },
                { value: stats.procedures, label: 'Procedures Listed', color: 'emerald' },
                { value: stats.case_studies, label: 'Case Studies Published', color: 'amber' },
                { value: stats.patient_views + stats.bookings, label: 'Patient Interactions', color: 'violet' },
              ].map(({ value, label, color }) => (
                <div key={label}>
                  <p className={`text-3xl font-bold text-${color}-600`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {value > 0 ? value.toLocaleString() : '—'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-32 bg-slate-50/80 scroll-mt-24" data-testid="how-it-works-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Simple Setup</p>
            <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 3rem)' }}>
              15 Minutes a Month. That's It.
            </h2>
            <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
              Set up once, publish monthly, and let the SEO engine work for you.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <div key={i} className="relative" data-testid={`step-${i}`}>
                <span className="text-6xl font-bold text-sky-100 block mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TurboCharge CTA — No pricing, directs to brochure */}
      <section id="turbocharge" className="py-20 md:py-28 bg-gradient-to-br from-sky-50 via-white to-emerald-50/30 scroll-mt-24" data-testid="turbocharge-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 border border-sky-200 text-sky-700 text-xs font-bold mb-6">
              <Rocket size={14} /> TURBOCHARGE YOUR VISIBILITY
            </div>
            <h2 className="font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 3rem)' }}>
              15 Minutes of Your Medical Expertise Each Month.
              <span className="block text-sky-600 mt-2">DocSite Handles the Rest.</span>
            </h2>
            <p className="text-slate-600 mt-4 text-lg max-w-2xl mx-auto">
              More patients find you on Google, organically. No ad spend. No middlemen. No scams. It was never this simple before.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* What doctor does */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h3 className="text-lg font-medium text-slate-900 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>What You Do (15 min/month)</h3>
              <div className="space-y-4">
                {[
                  { text: 'Complete your profile once', time: '10 min, once' },
                  { text: 'Add your procedures & locations', time: '15 min, once' },
                  { text: 'Publish 1 case study per month', time: '15 min/month' },
                  { text: 'Share your DocSite URL', time: '1 min' },
                ].map(({ text, time }) => (
                  <div key={text} className="flex items-start gap-3">
                    <CheckCircle size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-900">{text}</p>
                      <p className="text-xs text-slate-500">{time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What DocSite does */}
            <div className="bg-sky-50/50 rounded-2xl border border-sky-100 p-8">
              <h3 className="text-lg font-medium text-slate-900 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>What DocSite Does (Automatically)</h3>
              <div className="space-y-4">
                {[
                  'AI generates SEO meta tags & Schema.org markup',
                  'Auto-submits your pages to Google for indexing',
                  'Cross-links your procedures, articles & profile for SEO authority',
                  'Auto-generates specialty + city landing pages that rank for local searches',
                  'Publishes your articles on the Medical Insights hub for organic discovery',
                  'Tracks every lead — see who found you, from where, and who referred you',
                  'Patient referral links track clicks and appointments automatically',
                  'Your name shows up when patients search — no ad spend, no middlemen',
                ].map(text => (
                  <div key={text} className="flex items-start gap-3">
                    <Zap size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <button onClick={() => navigate('/brochure')} className="rounded-full px-10 py-4 bg-sky-600 text-white font-semibold hover:bg-sky-700 transition-all hover:shadow-lg hover:shadow-sky-600/25 inline-flex items-center gap-2 text-base" data-testid="turbo-cta-button">
              See All Plans & Compare <ArrowRight size={18} />
            </button>
            <p className="text-xs text-slate-500 mt-3">Continue with the Gold Plan forever, even without TurboCharge.</p>
          </div>
        </div>
      </section>

      {/* Why DocSite Exists — Mission Section */}
      <section className="py-16 md:py-24 bg-white border-t border-slate-100" data-testid="mission-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Why DocSite Exists</p>
            <h2 className="tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.4vw, 3rem)' }}>
              Democratizing Visibility for Doctors.
              <span className="block text-sky-600 mt-1">Removing Commercial Exploitation.</span>
            </h2>
            <p className="text-base text-slate-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              Today, doctors pay aggregator platforms to be "ranked" higher — not based on skill, but on ad spend. Patients see who pays the most, not who's best. DocSite changes this. Here, your real medical expertise is the content, and search engines rank you for it. No pay-to-appear. No commercial manipulation. Just your skills, reflected honestly.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'For Doctors', desc: 'Your visibility is earned through your expertise, not your wallet. Every procedure you describe, every article you publish — builds your organic presence. The more you know, the more patients find you.', color: 'sky' },
              { title: 'For Patients', desc: 'Find doctors based on real credentials, published expertise, and self-declared registrations — not paid rankings. Read what doctors actually know, not what they paid to advertise.', color: 'emerald' },
              { title: 'For Healthcare', desc: 'A platform where clinical competence drives discovery. Where good doctors in small cities have the same visibility tools as those in metro hospitals. Where the system works for both sides.', color: 'amber' },
            ].map(({ title, desc, color }) => (
              <div key={title} className={`rounded-2xl border border-${color}-200 bg-${color}-50/50 p-7`}>
                <h3 className="text-lg font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-sky-600" data-testid="cta-section">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-medium text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.875rem, 2.6vw, 3.25rem)' }}>
            Ready to Be Found by More Patients?
          </h2>
          <p className="text-sky-100 mt-4 text-lg max-w-xl mx-auto">
            Your medical expertise is the content. Google is the distribution. DocSite is the engine.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button onClick={() => navigate('/register?as=doctor&from=landing_bottom')} className="rounded-full px-8 py-3.5 bg-white text-sky-700 font-medium hover:bg-sky-50 transition-colors inline-flex items-center gap-2 text-base" data-testid="cta-get-started-button">
              Start My DocSite Practice <ArrowRight size={18} />
            </button>
            <button onClick={() => navigate('/brochure')} className="rounded-full px-8 py-3.5 bg-sky-500 text-white font-medium hover:bg-sky-400 transition-colors border border-sky-400 text-base" data-testid="cta-demo-button">
              Compare Plans
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// =============================================================================
// What's New strip — rendered just below the hero. Compact 2-row teaser of the
// latest 1-2 launches with NEW pulse-dot, deep-link to the dedicated 1-pager
// page, and a "View all launches" link. Powered by /data/featureLaunches.js
// so adding a new launch entry auto-bubbles into the hero.
// SEO bonus: home → /whats-new → /whats-new/{slug} forms a tight internal
// linking pyramid — boosts crawl frequency on the freshest content.
// =============================================================================
function WhatsNewStrip() {
  const top = FEATURE_LAUNCHES.slice(0, 2);
  if (!top.length) return null;
  return (
    <section className="py-8 md:py-10 bg-white border-t border-slate-100" aria-label="What's New" data-testid="home-whats-new-strip">
      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-sky-600 flex items-center gap-1.5">
            <Sparkles size={11} /> What's New on DocSite
          </p>
          <a href="/whats-new" className="text-xs font-semibold text-slate-600 hover:text-sky-700 inline-flex items-center gap-1" data-testid="home-whats-new-viewall">
            View all launches <ChevronRight size={12} />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {top.map(l => (
            <a key={l.slug} href={l.href}
               className="group rounded-2xl border border-slate-200 bg-gradient-to-br from-sky-50/40 via-white to-emerald-50/30 p-4 md:p-5 hover:border-sky-300 hover:shadow-sm transition flex items-start gap-3"
               data-testid={`home-whats-new-${l.slug}`}>
              <div className="flex flex-col items-center gap-1.5 shrink-0 pt-1">
                <time dateTime={l.published_at} className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  {new Date(l.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </time>
                {l.isNew && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    </span>
                    New
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm md:text-base font-semibold text-slate-900 group-hover:text-sky-700 transition-colors leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {l.title}
                </p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">{l.summary}</p>
                <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-sky-700 group-hover:text-sky-900">
                  Read the 1-pager <ArrowRight size={11} />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
